import { supabase } from '@/lib/supabase'
import type { Recipe } from '@/types/recipe'
import type { ShoppingList, ShoppingListItem } from '@/types/shopping-list'
import { ingredientAggregator } from './ingredients'
import { requireSessionUser } from './api-utils'
import { mealPlanApi } from './meal-plan-api'

const getShoppingLists = async (_householdId?: string, mealPlanId?: string): Promise<ShoppingList[]> => {
  let query = supabase
    .from('meal_plan_shopping_lists')
    .select('*')
    .order('created_at', { ascending: false })

  if (mealPlanId) {
    query = query.eq('meal_plan_id', mealPlanId)
  }

  const { data, error } = await query
  if (error) {
    throw error
  }

  return data ?? []
}

const updateShoppingList = async (shoppingListId: string, updates: Partial<ShoppingList>): Promise<ShoppingList> => {
  const { data, error } = await supabase
    .from('meal_plan_shopping_lists')
    .update(updates)
    .eq('id', shoppingListId)
    .select()
    .single()

  if (error) {
    throw error
  }

  return data
}

const updateShoppingListItem = async (
  shoppingListId: string,
  itemIndex: number,
  updates: Partial<ShoppingListItem>
): Promise<ShoppingList> => {
  const lists = await getShoppingLists(undefined, undefined)
  const current = lists.find((list) => list.id === shoppingListId)
  if (!current) {
    throw new Error('Shopping list not found')
  }

  const nextIngredients = [...(current.ingredients ?? [])]
  const baseItem = nextIngredients[itemIndex] ?? { name: 'Onbekend item' }
  nextIngredients[itemIndex] = {
    ...baseItem,
    ...updates,
  }

  return updateShoppingList(shoppingListId, { ingredients: nextIngredients })
}

const generateMealPlanShoppingList = async (
  mealPlanId: string,
  startDate?: string,
  endDate?: string
): Promise<ShoppingList> => {
  const user = await requireSessionUser()
  const plannedMeals = await mealPlanApi.getPlannedMeals(mealPlanId, startDate, endDate)
  const recipesForAggregation = [
    ...new Map(
      plannedMeals
        .map((meal) => meal.recipe)
        .filter((recipe): recipe is Recipe => Boolean(recipe))
        .map((recipe) => [recipe.id, recipe])
    ).values(),
  ]

  const aggregatedShoppingList = ingredientAggregator.aggregateFromPlannedMeals(
    plannedMeals,
    recipesForAggregation,
    startDate && endDate ? { start_date: startDate, end_date: endDate } : undefined
  )

  const ingredients = aggregatedShoppingList.all_items.map((item) => ({
    name: item.name,
    ingredient: item.name,
    quantity: item.quantity,
    unit: item.unit,
    specification: item.specification,
    recipe_ids: item.recipe_ids,
    recipe_titles: item.recipe_titles,
    category: item.category,
    is_purchased: Boolean(item.is_purchased),
    notes: item.notes ?? null,
  }))

  const payload = {
    meal_plan_id: mealPlanId,
    created_by: user.id,
    name: 'Boodschappenlijst',
    start_date: startDate ?? null,
    end_date: endDate ?? null,
    ingredients,
    auto_generated: true,
    is_completed: false,
  }

  const existingLists = await getShoppingLists(undefined, mealPlanId)
  if (existingLists.length > 0) {
    return updateShoppingList(existingLists[0].id, payload)
  }

  const { data, error } = await supabase
    .from('meal_plan_shopping_lists')
    .insert(payload)
    .select()
    .single()

  if (error) {
    throw error
  }

  return data
}

export const shoppingListApi = {
  getShoppingLists,
  generateMealPlanShoppingList,
  updateShoppingList,
  updateShoppingListItem,
}
